import { DEFAULT_RULE_PROFILE, NATURAL_RANKS, SEATS, SUITS, } from "./types.js";
const deck = Object.freeze([0, 1]
    .flatMap((deckIndex) => [
    ...SUITS.flatMap((suit) => NATURAL_RANKS.map((rank) => ({ deckIndex, suit, rank }))),
    { deckIndex, suit: "joker", rank: "SJ" },
    { deckIndex, suit: "joker", rank: "BJ" },
])
    .map((card, index) => Object.freeze({ ...card, id: `c${index}` })));
const cardById = new Map(deck.map((card) => [card.id, card]));
export function createDeck() {
    return deck;
}
export function getCard(card) {
    if (typeof card !== "string")
        return card;
    const found = cardById.get(card);
    if (!found)
        throw new RangeError(`Unknown card id: ${card}`);
    return found;
}
export function getCards(cards) {
    const resolved = cards.map(getCard);
    if (new Set(resolved.map((card) => card.id)).size !== resolved.length) {
        throw new RangeError("A physical card id may only appear once");
    }
    return resolved;
}
export function isNaturalRank(rank) {
    return NATURAL_RANKS.includes(rank);
}
export function naturalRankIndex(rank) {
    return NATURAL_RANKS.indexOf(rank);
}
export function naturalRankValue(rank) {
    return naturalRankIndex(rank) + 2;
}
export function rankStrength(rank, levelRank) {
    if (rank === "BJ")
        return 17;
    if (rank === "SJ")
        return 16;
    if (rank === levelRank)
        return 15;
    return naturalRankValue(rank);
}
export function isWildcard(card, levelRank) {
    const resolved = getCard(card);
    return resolved.suit === "hearts" && resolved.rank === levelRank;
}
export function resolveRuleProfile(profile) {
    return Object.freeze({ ...DEFAULT_RULE_PROFILE, ...profile });
}
export function normalizeSeed(seed) {
    return seed === undefined ? "guandan-default-seed" : String(seed);
}
export function hashSeed(seed) {
    const text = String(seed);
    let hash = 0x811c9dc5;
    for (let index = 0; index < text.length; index += 1) {
        hash ^= text.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193);
    }
    return hash >>> 0;
}
export function createSeededRandom(seed) {
    let state = hashSeed(seed);
    return () => {
        state = (state + 0x6d2b79f5) >>> 0;
        let value = state;
        value = Math.imul(value ^ (value >>> 15), value | 1);
        value ^= value + Math.imul(value ^ (value >>> 7), value | 61);
        return ((value ^ (value >>> 14)) >>> 0) / 4_294_967_296;
    };
}
export function shuffleCards(cards, seed) {
    const random = createSeededRandom(seed);
    const result = [...cards];
    for (let index = result.length - 1; index > 0; index -= 1) {
        const target = Math.floor(random() * (index + 1));
        const temporary = result[index];
        result[index] = result[target];
        result[target] = temporary;
    }
    return result;
}
export function sortCardIds(cards, context) {
    return [...cards].sort((leftId, rightId) => {
        const left = getCard(leftId);
        const right = getCard(rightId);
        const strength = rankStrength(left.rank, context.levelRank) -
            rankStrength(right.rank, context.levelRank);
        if (strength !== 0)
            return strength;
        const suitOrder = [
            "clubs",
            "diamonds",
            "hearts",
            "spades",
            "joker",
        ];
        const suit = suitOrder.indexOf(left.suit) - suitOrder.indexOf(right.suit);
        if (suit !== 0)
            return suit;
        return left.id.localeCompare(right.id, undefined, { numeric: true });
    });
}
export function shuffleAndDeal(seed = "guandan-default-seed", dealerSeat = 0, direction = "counter-clockwise") {
    const normalizedSeed = normalizeSeed(seed);
    const shuffledDeck = shuffleCards(deck.map((card) => card.id), normalizedSeed);
    const mutableHands = { 0: [], 1: [], 2: [], 3: [] };
    shuffledDeck.forEach((cardId, index) => {
        const offset = direction === "counter-clockwise" ? -index : index;
        const seat = ((dealerSeat + (offset % 4) + 4) % 4);
        mutableHands[seat].push(cardId);
    });
    const hands = Object.freeze(Object.fromEntries(SEATS.map((seat) => [seat, Object.freeze([...mutableHands[seat]])])));
    return Object.freeze({
        seed: normalizedSeed,
        dealerSeat,
        shuffledDeck: Object.freeze(shuffledDeck),
        hands,
    });
}
/**
 * Reproduces the official first-deal procedure in digital form: South reveals
 * a non-joker/non-heart-2 card, its point determines the first drawing seat,
 * cards are dealt counter-clockwise, and the holder of the revealed card leads.
 */
export function createOfficialOpeningDeal(seed = "guandan-default-seed") {
    const normalizedSeed = normalizeSeed(seed);
    const shuffledDeck = shuffleCards(deck.map((card) => card.id), normalizedSeed);
    const cutIndex = hashSeed(`${normalizedSeed}:official-opening-cut`) % shuffledDeck.length;
    let revealedIndex = cutIndex;
    for (let offset = 0; offset < shuffledDeck.length; offset += 1) {
        const candidateIndex = (cutIndex + offset) % shuffledDeck.length;
        const candidate = getCard(shuffledDeck[candidateIndex]);
        if (candidate.rank !== "SJ" &&
            candidate.rank !== "BJ" &&
            !(candidate.suit === "hearts" && candidate.rank === "2")) {
            revealedIndex = candidateIndex;
            break;
        }
    }
    const revealedCardId = shuffledDeck[revealedIndex];
    const revealedCard = getCard(revealedCardId);
    if (!isNaturalRank(revealedCard.rank))
        throw new Error("Official opening reveal must have a natural rank");
    // Seat 0 is South. South is counted as one, then counting continues in the
    // same counter-clockwise direction used for drawing and playing.
    const firstDrawSeat = ((0 -
        ((naturalRankValue(revealedCard.rank) - 1) % 4) +
        4) %
        4);
    const firstSeat = ((firstDrawSeat - (revealedIndex % 4) + 4) % 4);
    const dealt = shuffleAndDeal(normalizedSeed, firstDrawSeat);
    return Object.freeze({
        ...dealt,
        firstSeat,
        revealedCardId,
    });
}
export function cardIdOrder(left, right) {
    return Number(left.slice(1)) - Number(right.slice(1));
}
//# sourceMappingURL=cards.js.map